import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import type { RoleOrgType } from '../../auth/role-templates';
import type { UserSchema } from '../../types/auth.type';
import { buildOrgPermissionsDisplay } from '../../utils/permissions-display.helpers';

interface OrgPermissionsBreakdownProps {
  user: UserSchema;
  orgId: string;
  orgType: RoleOrgType;
  compact?: boolean;
}

type TintColor = 'primary' | 'success' | 'secondary' | 'info';

function tintedChipSx(theme: Theme, colorKey: TintColor = 'primary') {
  const color = theme.palette[colorKey].main;
  return {
    bgcolor: alpha(color, 0.15),
    color,
    fontWeight: 500,
    border: 'none',
    '& .MuiChip-label': { px: 1 },
  };
}

function TintedChip({
  label,
  colorKey = 'primary',
  sx,
}: {
  label: string;
  colorKey?: TintColor;
  sx?: object;
}) {
  const theme = useTheme();
  return (
    <Chip
      size="small"
      label={label}
      sx={{ ...tintedChipSx(theme, colorKey), ...sx }}
    />
  );
}

const OrgPermissionsBreakdown: React.FC<OrgPermissionsBreakdownProps> = ({
  user,
  orgId,
  orgType,
  compact = false,
}) => {
  const display = useMemo(
    () => buildOrgPermissionsDisplay(user, orgId, orgType),
    [user, orgId, orgType],
  );

  if (display.legacyRoles.length > 0 && display.groups.length === 0 && !display.fullOrgAccess) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {display.legacyRoles.map((role) => (
          <TintedChip
            key={role}
            label={role.replace(/_/g, ' ')}
            colorKey="secondary"
            sx={{ textTransform: 'capitalize' }}
          />
        ))}
      </Box>
    );
  }

  if (display.fullOrgAccess) {
    return <TintedChip label="Full organization access" colorKey="primary" />;
  }

  if (display.groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No module permissions assigned.
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 1.25 : 1.75}>
      {display.groups.map((group) => (
        <Box key={group.groupLabel}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.75 }}
          >
            {group.groupLabel}
          </Typography>
          <Stack spacing={compact ? 0.5 : 0.75}>
            {group.entities.map((ent) => (
              <Stack
                key={ent.entity}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.25, sm: 1 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: compact ? 110 : 140, flexShrink: 0 }}
                >
                  {ent.entityLabel}
                </Typography>
                {ent.allActions ? (
                  <TintedChip label="Full access" colorKey="success" />
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {ent.actions.map((action) => (
                      <TintedChip key={action} label={action} colorKey="primary" />
                    ))}
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default OrgPermissionsBreakdown;
